<template>
    <div class="ts-branch-sub-filter" ref="wrapper">
        <div class="ivu-select ivu-select-multiple" :class="{ 'ivu-select-visible': visible }" @click="toggleDropdown">
            <div class="ivu-select-selection">
                <span v-if="selectedTags.length === 0" class="ivu-select-placeholder">{{ $t('all') }}</span>
                <template v-else>
                    <span class="ivu-tag ivu-tag-checked" v-for="tag in displayTags" :key="tag.branch_id">
                        <span class="ivu-tag-text">{{ tag.branch_name_en }}</span>
                        <i class="ivu-icon ivu-icon-ios-close" @click.stop="removeSelection(tag.branch_id)"></i>
                    </span>
                    <span v-if="moreCount > 0" class="ivu-tag ivu-tag-checked">
                        <span class="ivu-tag-text">+{{ moreCount }}</span>
                    </span>
                </template>
                <i class="ivu-icon ivu-icon-ios-arrow-down ivu-select-arrow"></i>
            </div>
        </div>
        <transition name="transition-drop">
            <div class="ivu-select-dropdown" v-show="visible" ref="dropdown" style="max-height: 300px; overflow-y: auto;">
                <div style="padding: 6px 8px;">
                    <Input v-model="searchText" size="small" :placeholder="$t('search')" clearable icon="ios-search" @click.native.stop />
                </div>
                <ul class="ivu-select-dropdown-list">
                    <template v-for="branch in filteredMainBranches">
                        <li :key="branch.branch_id" class="ivu-select-item"
                            :class="{ 'ivu-select-item-selected': isSelected(branch.branch_id) }"
                            @click.stop="onToggleBranch(branch.branch_id)">
                            <div style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                                <span>
                                    <Checkbox :value="isSelected(branch.branch_id)" @click.native.stop @on-change="() => onToggleBranch(branch.branch_id)">
                                        {{ branch.branch_name_en }}
                                    </Checkbox>
                                </span>
                                <span v-if="getChildren(branch.branch_id).length > 0"
                                      class="ts-sub-toggle-btn"
                                      :class="{ active: isExpanded(branch.branch_id) }"
                                      @click.stop="toggleExpandSubs(branch.branch_id)"
                                      :title="$t('branch.includeSubBranches')">
                                    <i class="fa fa-sitemap"></i>
                                </span>
                            </div>
                        </li>
                        <template v-if="isExpanded(branch.branch_id)">
                            <li v-for="sub in getFilteredChildren(branch.branch_id)"
                                :key="sub.branch_id"
                                class="ivu-select-item"
                                :class="{ 'ivu-select-item-selected': isSelected(sub.branch_id) }"
                                style="padding-left: 32px;"
                                @click.stop="onToggleBranch(sub.branch_id)">
                                <Checkbox :value="isSelected(sub.branch_id)" @click.native.stop @on-change="() => onToggleBranch(sub.branch_id)">
                                    &#8627; {{ sub.branch_name_en }}
                                </Checkbox>
                            </li>
                        </template>
                    </template>
                </ul>
            </div>
        </transition>
    </div>
</template>

<script>
export default {
    name: 'TsBranchWithSubFilter',
    props: {
        value: {
            type: Array,
            default: () => []
        },
        branches: {
            type: Array,
            default: () => []
        }
    },
    data () {
        return {
            selected: this.value ? [...this.value] : [],
            visible: false,
            searchText: '',
            expandedParents: {}
        }
    },
    computed: {
        mainBranches () {
            return this.branches.filter(b => !b.is_sub)
        },
        filteredMainBranches () {
            if (!this.searchText) return this.mainBranches
            const search = this.searchText.toLowerCase()
            return this.mainBranches.filter(b => {
                if (b.branch_name_en.toLowerCase().includes(search)) return true
                return this.getChildren(b.branch_id).some(
                    sub => sub.branch_name_en.toLowerCase().includes(search)
                )
            })
        },
        selectedTags () {
            return this.branches.filter(b => this.selected.includes(b.branch_id))
        },
        displayTags () {
            return this.selectedTags.slice(0, 1)
        },
        moreCount () {
            return Math.max(0, this.selectedTags.length - 1)
        }
    },
    methods: {
        toggleDropdown () {
            this.visible = !this.visible
        },
        onDocumentClick (e) {
            if (this.$refs.wrapper && !this.$refs.wrapper.contains(e.target)) {
                this.visible = false
            }
        },
        isSelected (branchId) {
            return this.selected.includes(branchId)
        },
        isExpanded (parentId) {
            return !!this.expandedParents[parentId]
        },
        getChildren (parentId) {
            return this.branches.filter(b => b.is_sub && b.parent_id === parentId)
        },
        getFilteredChildren (parentId) {
            const children = this.getChildren(parentId)
            if (!this.searchText) return children
            const search = this.searchText.toLowerCase()
            return children.filter(b => b.branch_name_en.toLowerCase().includes(search))
        },
        onToggleBranch (branchId) {
            const idx = this.selected.indexOf(branchId)
            if (idx > -1) {
                this.selected.splice(idx, 1)
            } else {
                this.selected.push(branchId)
            }
            this.emitValue()
        },
        removeSelection (branchId) {
            const idx = this.selected.indexOf(branchId)
            if (idx > -1) {
                this.selected.splice(idx, 1)
            }
            // If removed branch is a main branch, also remove its subs and collapse
            const children = this.getChildren(branchId)
            if (children.length > 0) {
                const childIds = children.map(c => c.branch_id)
                this.selected = this.selected.filter(id => !childIds.includes(id))
                this.$set(this.expandedParents, branchId, false)
            }
            this.emitValue()
        },
        toggleExpandSubs (parentId) {
            const isExpanded = this.isExpanded(parentId)
            this.$set(this.expandedParents, parentId, !isExpanded)

            const children = this.getChildren(parentId)
            const childIds = children.map(c => c.branch_id)

            if (!isExpanded) {
                // Expanding: auto-select all sub-branches
                childIds.forEach(id => {
                    if (!this.selected.includes(id)) {
                        this.selected.push(id)
                    }
                })
            } else {
                // Collapsing: deselect all sub-branches
                this.selected = this.selected.filter(id => !childIds.includes(id))
            }
            this.emitValue()
        },
        emitValue () {
            this.$emit('input', [...this.selected])
            this.$emit('on-change', [...this.selected])
        }
    },
    watch: {
        value (val) {
            this.selected = val ? [...val] : []
        }
    },
    mounted () {
        document.addEventListener('click', this.onDocumentClick)
    },
    beforeDestroy () {
        document.removeEventListener('click', this.onDocumentClick)
    }
}
</script>

<style scoped>
.ts-branch-sub-filter {
    position: relative;
}
.ivu-select-dropdown {
    position: absolute;
    width: 100%;
    z-index: 900;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.2);
}
.ts-sub-toggle-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 3px;
    cursor: pointer;
    color: #808695;
    font-size: 12px;
    transition: all 0.2s;
}
.ts-sub-toggle-btn:hover {
    background: #e8eaec;
    color: #515a6e;
}
.ts-sub-toggle-btn.active {
    background: #2d8cf0;
    color: #fff;
}
</style>
